import Helper from '../../../Helper';
import { thumbnailClick, mediaClick as publisherMediaClick } from '../../../../publisher';

/**
 * Helper function to create large image configuration for magnify component
 * @param {string} largeImage - URL of the large image
 * @param {boolean} highResImage - Whether to use high resolution dimensions
 * @returns {Object} Large image configuration object
 * @private
 */
const magnifyLargeImage = (largeImage, highResImage) => {
  return {
    src: largeImage,
    width: highResImage ? 2000 : 1000,
    height: highResImage ? 2000 : 1000
  };
};

/**
 * Generates button title text based on thumbnail type and position
 * @param {boolean} isLast - Whether this is the last thumbnail
 * @param {boolean} isInteractiveMedia - Whether the thumbnail represents interactive media
 * @param {Object} thumbnail - The thumbnail object
 * @param {number} thumbnailCount - Total number of thumbnails
 * @returns {string} The button title text
 */
export const getButtonTitle = (isLast, isInteractiveMedia, thumbnail, thumbnailCount) => {
  if (isLast) {
    return `View all ${thumbnailCount} media`;
  }
  if (isInteractiveMedia) {
    return `View ${thumbnail?.type?.toLowerCase()}`;
  }
  return 'View image';
};

/**
 * Returns props for the ReactImageMagnify component
 * @param {Object} params - Configuration parameters
 * @param {string} params.smallImageAlt - Alt text for the small image
 * @param {string} params.url - URL for the small image
 * @param {string} params.mainImageFetchPriority - Fetch priority for the image
 * @param {string} params.largeImage - URL for the large image
 * @param {boolean} params.highResImage - Whether to use high resolution image
 * @returns {Object} ReactImageMagnify props configuration
 */
export const getImageMagnifyProps = ({
  smallImageAlt,
  url,
  mainImageFetchPriority,
  largeImage,
  highResImage
}) => {
  return {
    smallImage: {
      alt: smallImageAlt,
      isFluidWidth: true,
      src: url,
    },
    largeImage: magnifyLargeImage(largeImage, highResImage),
    lensStyle: {
      backgroundImage: '',
    },
    enlargedImageContainerClassName:
      'sui-bg-primary sui-border sui-border-solid sui-border-1 '
      + 'sui-border-primary sui-z-tooltip',
    enlargedImageContainerDimensions: {
      width: '100%',
      height: '100%',
    },
    fetchPriority: mainImageFetchPriority,
  };
};

/**
 * Generates media event properties for thumbnail interactions in a media gallery.
 *
 * @param {boolean} grid - Flag indicating if the gallery is in grid layout mode
 * @param {Function} onThumbnailEvent - Callback function to handle thumbnail events
 * @param {Array} availableThumbnails - Array of available thumbnail assets
 * @param {Array} mainImageList - Array of main image assets
 * @param {Object} thumbnailAsset - The specific thumbnail asset for which events are being generated
 * @returns {Object} Object containing event handler properties (onClick, onMouseEnter, onMouseLeave)
 */
export const getMediaEventProps = (
  grid,
  onThumbnailEvent,
  availableThumbnails,
  mainImageList,
  thumbnailAsset
) => {
  const baseOnClick = () => onThumbnailEvent(
    availableThumbnails,
    mainImageList,
    thumbnailAsset,
    thumbnailAsset,
    'click'
  );

  if (grid) {
    return {
      onClick: baseOnClick,
    };
  }

  return {
    onClick: baseOnClick,
    onMouseEnter: () => {
      onThumbnailEvent(
        availableThumbnails,
        mainImageList,
        thumbnailAsset,
        thumbnailAsset,
        'mouseenter'
      );
    },
    onMouseLeave: () => {
      onThumbnailEvent(
        availableThumbnails,
        mainImageList,
        thumbnailAsset,
        thumbnailAsset,
        'mouseout'
      );
    },
  };
};

/**
 * Converts an organized thumbnail index back to its original index in the unorganized array
 * @param {number} organizedIndex - Index in the organized thumbnails array
 * @param {Array} organizedThumbs - Array of organized thumbnails
 * @param {Array} originalThumbs - Array of original thumbnails
 * @returns {number} The corresponding index in the original thumbnails array
 */
export const getOriginalIndexFromOrganized = (organizedIndex, organizedThumbs, originalThumbs) => {
  if (organizedIndex < 0 || organizedIndex >= organizedThumbs.length) return 0;

  const organizedThumbnail = organizedThumbs[organizedIndex];
  const originalIndex = originalThumbs.findIndex((t) => t.id === organizedThumbnail.id);
  return originalIndex !== -1 ? originalIndex : 0;
};

/**
 * Returns metadata for a thumbnail, including computed URLs and flags
 * @param {Object} thumbnail - The thumbnail object
 * @param {number} thumbnailIndex - Index of the thumbnail
 * @param {number} totalThumbnailsToShow - Max number of thumbnails to show
 * @returns {Object} Thumbnail metadata object with computed properties
 */
export function getThumbnailMeta(thumbnail, thumbnailIndex, totalThumbnailsToShow) {
  const isAR = Helper?.isAR({ media: thumbnail });
  const isAvataarAR = Helper?.isAvataarAR({ media: thumbnail });
  const isVideo = Helper?.isVideo({ media: thumbnail });
  const isThreesixty = Helper?.isImage360({ media: thumbnail });
  const isHotspot = Helper?.isHotspot({ media: thumbnail });
  const isCustomColor = Helper?.isCustomColor({ media: thumbnail });
  const isManualColor = Helper?.isManualColor({ media: thumbnail });
  const {
    id, url = '', videoStill = '', mediaStill = '', altText, brandLogo, rgb
  } = thumbnail;
  const alt = altText || id;

  let thumbnailUrl = url || '';
  if (isAR) {
    thumbnailUrl = mediaStill || '';
  } else if (isVideo) {
    thumbnailUrl = videoStill || '';
  } else if (isThreesixty) {
    thumbnailUrl = url ? `${url}?thumb&profile=400` : '';
  }

  const isLastThumbnail = thumbnailIndex === (totalThumbnailsToShow - 1);
  const isImagePath = (/^(?=.*\/productImages\/).*$/.test(thumbnailUrl));
  const reducedThumbnailUrl = thumbnailUrl ? thumbnailUrl.replace('_145.', '_100.') : '';
  const finalReducedThumbnailUrl = reducedThumbnailUrl
    ? reducedThumbnailUrl.replace('catalog/productImages/145', 'catalog/productImages/100')
    : '';

  let finalThumbnailUrl = thumbnailUrl;
  if (isVideo) {
    finalThumbnailUrl = thumbnailUrl ? thumbnailUrl.replace('/1280x720/match', '/88x50/match') : '';
  }

  return {
    alt,
    altText,
    brandLogo,
    finalReducedThumbnailUrl,
    finalThumbnailUrl,
    id,
    isAR,
    isAvataarAR,
    isCustomColor,
    isHotspot,
    isImagePath,
    isLastThumbnail,
    isManualColor,
    isThreesixty,
    isVideo,
    mediaStill,
    reducedThumbnailUrl,
    rgb,
    thumbnailUrl,
    url,
    videoStill,
  };
}

/**
 * Gets the media type from an asset object
 * @param {Object} asset - The asset object to analyze
 * @returns {string} The asset type (IMAGE, VIDEO, THREESIXTY, AR, HOTSPOTS, CUSTOMER_IMAGE)
 */
export const getTypeFromAsset = (asset) => {
  if (Helper.isImage({ media: asset })) return 'IMAGE';
  if (Helper.isVideo({ media: asset })) return 'VIDEO';
  if (Helper.isImage360({ media: asset })) return 'THREESIXTY';
  if (Helper.isAR({ media: asset })) return 'AR';
  if (Helper.isHotspot({ media: asset })) return 'HOTSPOTS';
  if (asset?.type === 'CUSTOMER_IMAGE') return 'CUSTOMER_IMAGE';
  return 'IMAGE';
};

/**
 * Hides the overlay and optionally triggers Avataar button click callback
 * @param {Function} setOverlayOpened - State setter for overlay open state
 * @param {Function} [onAvataarButtonClick] - Optional callback for Avataar button click
 * @returns {void}
 */
export const handleHideOverlay = (setOverlayOpened, onAvataarButtonClick) => {
  setOverlayOpened(false);
  if (onAvataarButtonClick) {
    onAvataarButtonClick();
  }
};

/**
 * Handles main image click event, updating overlay state and triggering analytics
 * @param {Object} params - Configuration parameters
 * @param {string} params.thumbnailType - Type of the thumbnail
 * @param {number} params.augmentedRealityIndex - Index for AR image
 * @param {number} params.selectedThumbnailIndex - Index of selected thumbnail
 * @param {Object} params.mainImage - The main image object
 * @param {Function} params.setOverlayOpened - State setter for overlay open state
 * @param {Function} params.setSelectedThumbnailIndex - State setter for selected thumbnail index
 * @param {Array} params.organizedThumbs - Array of organized thumbnails
 * @param {Array} params.originalThumbnails - Array of original thumbnails
 * @param {Function} params.setOverlaySelectedIndex - State setter for overlay selected index
 * @param {Function} params.setOverlayInitialType - State setter for overlay initial type
 * @param {Function} params.setOverlayInitialId - State setter for overlay initial ID
 * @returns {void}
 */
export const handleMainImageClick = ({
  thumbnailType,
  augmentedRealityIndex,
  selectedThumbnailIndex,
  mainImage,
  setOverlayOpened,
  organizedThumbs,
  originalThumbnails,
  setOverlaySelectedIndex,
  setOverlayInitialType,
  setOverlayInitialId,
  setSelectedThumbnailIndex,
}) => {
  const payload = {
    eventName: 'expanded-view',
    primaryCategory: 'overlay'
  };
  thumbnailClick(payload);

  const selectedIndex = (thumbnailType === 'AVATAAR_AR') ? augmentedRealityIndex : selectedThumbnailIndex;
  const mediaType = Helper?.getMediaType(mainImage);

  const thumbnailPayload = {
    section: 'media',
    component: 'thumbnails',
    target: mediaType,
    position: 1 + selectedIndex,
    guid: mediaType === 'image' ? Helper?.getImageGuid(mainImage) : 'n/a'
  };

  publisherMediaClick(thumbnailPayload);
  const originalIndex = getOriginalIndexFromOrganized(
    selectedThumbnailIndex,
    organizedThumbs,
    originalThumbnails
  );
  setOverlaySelectedIndex(originalIndex);
  setOverlayInitialType(getTypeFromAsset(mainImage));
  setOverlayInitialId(mainImage?.id);
  setOverlayOpened(true);
  setSelectedThumbnailIndex(selectedIndex);

  if (typeof window !== 'undefined') {
    window.history.pushState({ page: 'MEDIAOVERLAY' }, null, '');
  }
};

/**
 * Handles thumbnail events such as click and mouseenter
 * @param {Object} params - Configuration parameters
 * @param {Array} params.thumbnailList - List of thumbnails
 * @param {Array} params.images - List of images
 * @param {Object} params.thumbnail - The thumbnail object
 * @param {string} params.eventType - The event type ('click', 'mouseenter', etc.)
 * @param {number} params.totalThumbnailsToShow - Max number of thumbnails to show
 * @param {number} params.originalThumbnailCount - Total count of original thumbnails
 * @param {Object} params.mainImage - The main image object
 * @param {Function} params.setSelectedThumbnailIndex - State setter for selected thumbnail index
 * @param {Function} params.setMainImage - State setter for main image
 * @param {Function} params.setThumbnails - State setter for thumbnails
 * @param {Function} params.setOverlayOpened - State setter for overlay open state
 * @param {Function} params.setOverlayInitialType - State setter for overlay initial type
 * @param {Function} params.setOverlayInitialId - State setter for overlay initial ID
 * @param {number} params.augmentedRealityIndex - Index for AR
 * @param {Object} params.Helper - Helper utilities object
 * @returns {null} Always returns null
 */
export const handleThumbnailEvent = ({
  thumbnailList,
  images,
  thumbnail,
  eventType,
  totalThumbnailsToShow,
  originalThumbnailCount,
  mainImage,
  setSelectedThumbnailIndex,
  setMainImage,
  setThumbnails,
  setOverlayOpened,
  setOverlayInitialType,
  setOverlayInitialId,
  augmentedRealityIndex,
}) => {
  const index = thumbnailList.findIndex((t) => t.id === thumbnail.id);
  const totalCount = originalThumbnailCount || thumbnailList.length;
  const isLastThumbnail = totalThumbnailsToShow && index === (totalThumbnailsToShow - 1);

  switch (eventType) {
    case 'click': {
      if (index === -1) return null;

      setMainImage(images[index]);
      setSelectedThumbnailIndex(index);

      if (isLastThumbnail && totalCount > totalThumbnailsToShow) {
        if (setOverlayInitialType) setOverlayInitialType('IMAGE');
        if (setOverlayInitialId) setOverlayInitialId(thumbnail.id);
        const payload = {
          eventName: 'expanded-view',
          primaryCategory: 'overlay'
        };
        thumbnailClick(payload);
        setOverlayOpened(true);
      }

      const isInteractiveMedia = Helper?.isVideo({ media: thumbnail })
                                || Helper?.isAR({ media: thumbnail })
                                || Helper?.isImage360({ media: thumbnail })
                                || Helper?.isHotspot({ media: thumbnail })
                                || Helper?.isCustomColor({ media: thumbnail })
                                || Helper?.isManualColor({ media: thumbnail });

      const mediaType = Helper?.getMediaType(thumbnail);
      const payload = {
        section: 'media',
        component: 'thumbnails',
        target: mediaType,
        position: index + 1,
        guid: mediaType === 'image' ? Helper?.getImageGuid(thumbnail) : 'n/a'
      };

      if (typeof publisherMediaClick === 'function') {
        publisherMediaClick(payload);
      }

      if (isInteractiveMedia) {
        const type = getTypeFromAsset(thumbnail);
        if (setOverlayInitialType) setOverlayInitialType(type);
        if (setOverlayInitialId) setOverlayInitialId(thumbnail.id);
        setOverlayOpened(true);

        const expandedPayload = {
          eventName: 'expanded-view',
          primaryCategory: 'overlay'
        };
        thumbnailClick(expandedPayload);
      }
      break;
    }
    case 'mouseenter': {
      if (index !== -1) {
        setMainImage(images[index]);
        setSelectedThumbnailIndex(index);
      }
      break;
    }
    default: break;
  }
  return null;
};

/**
 * Organizes thumbnails according to specific display rules
 * - Positions 1-4: Images first, then videos (no interleaving)
 * - Positions 5-6: Other media types (360, AR, measurements, etc.)
 * - Position 7: Last thumbnail with count (must be IMAGE type)
 * - If not enough other media types, fill with remaining images/videos
 * @param {Array} [thumbnailList=[]] - Array of thumbnail objects to organize
 * @param {number} [maxThumbnails=7] - Maximum number of thumbnails to show
 * @returns {Array} Array of organized thumbnails
 */
export const organizeThumbnails = (thumbnailList = [], maxThumbnails = 7) => {
  if (!Array.isArray(thumbnailList) || thumbnailList.length === 0) {
    return [];
  }

  if (thumbnailList.length <= maxThumbnails) {
    return thumbnailList;
  }

  const images = thumbnailList.filter((item) => Helper.isImage({ media: item }) && !Helper.isHotspot({ media: item }));
  const videos = thumbnailList.filter((item) => Helper.isVideo({ media: item }));
  const otherMedia = thumbnailList.filter((item) => Helper.isAR({ media: item })
    || Helper.isImage360({ media: item })
    || Helper.isHotspot({ media: item })
    || Helper.isCustomColor({ media: item })
    || Helper.isManualColor({ media: item })
  );

  const organized = [];
  const interactiveMedia = [...videos, ...otherMedia];

  for (let i = 0; i < Math.min(4, images.length) && organized.length < 4; i++) {
    organized.push(images[i]);
  }

  let imagesUsed = organized.length;
  let interactiveUsed = 0;

  for (let slot = 5; slot <= 6 && organized.length < maxThumbnails - 1; slot++) {
    if (interactiveUsed < interactiveMedia.length
        && (imagesUsed >= images.length || interactiveMedia.length >= 2 - (slot - 5))) {
      organized.push(interactiveMedia[interactiveUsed]);
      interactiveUsed++;
    } else if (imagesUsed < images.length) {
      organized.push(images[imagesUsed]);
      imagesUsed++;
    }
  }

  if (organized.length < maxThumbnails && imagesUsed < images.length) {
    organized.push(images[imagesUsed]);
  }

  return organized;
};

/**
 * Updates the page layout to show the first main image and its thumbnails
 * @param {Object} media - The media object containing main images and thumbnails
 * @param {Function} setMainImage - State setter for main image
 * @param {Function} setSelectedThumbnailIndex - State setter for selected thumbnail index
 * @param {Function} setThumbnails - State setter for thumbnails
 * @returns {void}
 */
export const updatePageLayout = (media, setMainImage, setSelectedThumbnailIndex, setThumbnails) => {
  const { main, thumbnails: mediaThumbnails = [] } = media;
  setMainImage(main[0]);
  setSelectedThumbnailIndex(0);
  setThumbnails(mediaThumbnails);
};

/**
 * Updates the page layout after super SKU hover ends
 * @param {Object} media - The media object containing main images and thumbnails
 * @param {number} selectedThumbnailIndex - Index of selected thumbnail
 * @param {Function} setMainImage - State setter for main image
 * @param {Function} setThumbnails - State setter for thumbnails
 * @returns {void}
 */
export const updatePagePostSuperSkuHoverLayout = (
  media,
  selectedThumbnailIndex,
  setMainImage,
  setThumbnails
) => {
  const { main, thumbnails: mediaThumbnails = [] } = media;
  setMainImage(main[selectedThumbnailIndex]);
  setThumbnails(mediaThumbnails);
};

/**
 * Updates the page layout when hovering over a super SKU
 * @param {Object} media - The media object containing main images
 * @param {Array} mainSuperSkuHover - Array of hovered super SKU images
 * @param {number} selectedThumbnailIndex - Index of selected thumbnail
 * @param {Function} setMainImage - State setter for main image
 * @param {Object} Helper - Helper utilities object
 * @returns {void}
 */
export const updatePageSuperSkuHoverLayout = (
  media,
  mainSuperSkuHover,
  selectedThumbnailIndex,
  setMainImage,
) => {
  const { main } = media;
  const index = mainSuperSkuHover.findIndex((t) => t.type === main[selectedThumbnailIndex]?.type);
  const isImage = Helper?.isImage({ media: mainSuperSkuHover[index] });
  setMainImage((isImage && index > 0) ? mainSuperSkuHover[index] : mainSuperSkuHover[0]);
};